import { ENCRYPTION_KEY } from '@/constants/api';
import { STORAGE_KEYS } from '@/constants/storage';
import { decryptPayload, isEncryptedPayload } from '@/utils/crypto';
import { encryptedStorage } from '@/utils/encrypted-storage';
import Storage, { StorageTypes } from '@/utils/storage';

let tokenCache: string | null = null;

export function readSessionToken(): string | null {
  if (tokenCache === null) {
    return null;
  }
  if (tokenCache.startsWith('{')) {
    try {
      if (isEncryptedPayload(JSON.parse(tokenCache) as unknown)) {
        tokenCache = null;
        return null;
      }
    } catch {
      // not an envelope object
    }
  }
  return tokenCache;
}

export async function hydrateSessionTokenFromStorage(): Promise<string | null> {
  const fromEncrypted = await encryptedStorage.get<string>(STORAGE_KEYS.TOKEN);
  if (typeof fromEncrypted === 'string' && fromEncrypted.trim().length > 0) {
    tokenCache = fromEncrypted.trim();
    return tokenCache;
  }

  const legacyRaw = Storage.getStorageData(StorageTypes.LOCAL, STORAGE_KEYS.TOKEN);
  if (typeof legacyRaw !== 'string' || legacyRaw.trim().length === 0) {
    tokenCache = null;
    return null;
  }

  const trimmed = legacyRaw.trim();

  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (isEncryptedPayload(parsed)) {
      if (!ENCRYPTION_KEY) {
        Storage.remove(StorageTypes.LOCAL, STORAGE_KEYS.TOKEN);
        encryptedStorage.remove(STORAGE_KEYS.TOKEN);
        tokenCache = null;
        return null;
      }
      try {
        const decrypted = await decryptPayload(parsed, ENCRYPTION_KEY);
        if (typeof decrypted === 'string' && decrypted.trim().length > 0) {
          const jwt = decrypted.trim();
          tokenCache = jwt;
          Storage.remove(StorageTypes.LOCAL, STORAGE_KEYS.TOKEN);
          await encryptedStorage.set(STORAGE_KEYS.TOKEN, jwt);
          return tokenCache;
        }
      } catch {
        // stale envelope
      }
      Storage.remove(StorageTypes.LOCAL, STORAGE_KEYS.TOKEN);
      encryptedStorage.remove(STORAGE_KEYS.TOKEN);
      tokenCache = null;
      return null;
    }
    if (typeof parsed === 'string' && parsed.trim().length > 0) {
      const jwt = parsed.trim();
      tokenCache = jwt;
      Storage.remove(StorageTypes.LOCAL, STORAGE_KEYS.TOKEN);
      await encryptedStorage.set(STORAGE_KEYS.TOKEN, jwt);
      return tokenCache;
    }
  } catch {
    // not JSON — legacy raw JWT
  }

  tokenCache = trimmed;
  Storage.remove(StorageTypes.LOCAL, STORAGE_KEYS.TOKEN);
  await encryptedStorage.set(STORAGE_KEYS.TOKEN, trimmed);
  return tokenCache;
}

export async function writeSessionToken(token: string): Promise<void> {
  const trimmed = token.trim();
  tokenCache = trimmed;
  Storage.remove(StorageTypes.LOCAL, STORAGE_KEYS.TOKEN);
  await encryptedStorage.set(STORAGE_KEYS.TOKEN, trimmed);
}

export function clearTokenCache(): void {
  tokenCache = null;
}

export function clearSession(): void {
  clearTokenCache();
  encryptedStorage.remove(STORAGE_KEYS.TOKEN);
  Storage.remove(StorageTypes.LOCAL, STORAGE_KEYS.TOKEN);
}

export function isDevSessionRequestUrl(url: string | undefined): boolean {
  if (!url) {
    return false;
  }
  return url.toLowerCase().includes('dev-session');
}
